/*
  IW Nuevo: "Vistos recientemente" on the home (phase 2.5, T37 and T38; decision D38). Markup:
  sections/iw-home-recent.liquid.

  The product page stores the handle it shows (snippets/iw-recent-record.liquid): localStorage "iw:recent", a JSON
  array, newest first, deduplicated, at most 8. This element reads that list, fetches one card per handle through
  the Section Rendering API (/products/<handle>?section_id=iw-recent-card, sections/iw-recent-card.liquid, the home
  rows' card) and reveals the section once at least one card loaded.

  - Nothing is fetched when the list is empty or unreadable (storage blocked or throwing, invalid JSON).
  - A handle must match /^[a-z0-9-]+$/ before it goes into a URL; anything else is ignored. The handle of the page
    itself (data-exclude) is skipped; the home has none.
  - Up to 3 cards (data-limit). A handle that fails (missing product, network error, unexpected markup) is skipped
    and the next stored handle takes its place. Each handle is requested at most once per element, so there are
    never more than 8 requests, even when the element is moved and starts over.
  - A missing or unpublished product is pruned from "iw:recent". Shopify answers such a request with 200 and our
    section rendered empty (sections/iw-recent-card.liquid renders nothing without a product), so "our section,
    no card" is the proof. A 404 means the section itself is unknown (theme problem), so nothing is pruned then;
    network errors and other statuses prune nothing either.
  - Only the card inside the [data-iw-recent-card] marker of our section's response is inserted. It is parsed
    inertly through a <template> (no script runs and no image loads before insertion), and script elements are
    removed. Two handles that resolve to the same product (a redirect) show one card.
  - Timing: after the window load event, once the section is within 800px of the viewport, on idle. It never
    competes with the LCP image.
  - No layout shift in view: the section stays display:none until it is filled. If its spot is on screen when the
    cards arrive, the reveal waits until it is scrolled out of view. Revealed above the viewport, browsers with
    scroll anchoring keep the visible content still; the others get the same correction by script.
  - Moved in the DOM (the theme editor reorders sections): disconnecting invalidates the pending run; reconnecting
    starts a new run, or only re-arms the reveal when the cards are already in.
*/
(function () {
  if (!window.customElements || window.customElements.get('iw-home-recent')) return;

  var STORAGE_KEY = 'iw:recent';
  var SECTION = 'iw-recent-card';
  var HANDLE = /^[a-z0-9-]+$/;
  var MAX_STORED = 8;

  // Every storage access is wrapped: the getter itself throws when site data is blocked.
  function readHandles() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return [];
    }
    if (!raw) return [];

    var list;
    try {
      list = JSON.parse(raw);
    } catch (error) {
      return [];
    }
    if (!Array.isArray(list)) return [];

    var handles = [];
    for (var i = 0; i < list.length && handles.length < MAX_STORED; i++) {
      var item = list[i];
      if (typeof item === 'string' && HANDLE.test(item) && handles.indexOf(item) === -1) handles.push(item);
    }
    return handles;
  }

  // Removes one handle from the stored list, re-read right before writing so a view recorded meanwhile is kept.
  function forget(handle) {
    try {
      var store = window.localStorage;
      var list = JSON.parse(store.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(list)) return;
      var kept = list.filter(function (item) {
        return item !== handle;
      });
      if (kept.length !== list.length) store.setItem(STORAGE_KEY, JSON.stringify(kept));
    } catch (error) {}
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
    } else {
      window.addEventListener('load', callback, { once: true });
    }
  }

  function onIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2000 });
    } else {
      window.setTimeout(callback, 200);
    }
  }

  // Reads our section's response: { card } when it holds a card, { missing: true } when our section rendered
  // without one (no such product), null for anything else.
  function parseCard(html) {
    var template = document.createElement('template');
    template.innerHTML = html;
    var content = template.content;
    var marker = content.querySelector('[data-iw-recent-card]');
    if (!marker) return content.querySelector('#shopify-section-' + SECTION) ? { missing: true } : null;
    var card = marker.querySelector('.card-wrapper--iw');
    if (!card || !HANDLE.test(card.getAttribute('data-handle') || '')) return null;
    var scripts = card.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) scripts[i].remove();
    return { card: document.importNode(card, true) };
  }

  class IwHomeRecent extends HTMLElement {
    connectedCallback() {
      this.box = this.querySelector('.iw-recent');
      this.list = this.querySelector('.iw-recent__grid');
      if (!this.box || !this.list) return;
      this.designMode = this.hasAttribute('data-design-mode');

      // Back in the page after the cards were inserted: only the reveal can still be pending.
      if (this.rendered) {
        if (this.box.hidden && !this.designMode) this.revealWhenOffScreen();
        return;
      }
      this.start();
    }

    disconnectedCallback() {
      // Invalidates every pending step of the current run; a reconnect starts a new one.
      this.run = (this.run || 0) + 1;
      this.stopObserving();
    }

    // True while the run that scheduled a step is still the element's current run.
    alive(run) {
      return run === this.run && this.isConnected;
    }

    start() {
      var run = (this.run = (this.run || 0) + 1);
      var exclude = this.dataset.exclude || '';
      this.handles = readHandles().filter(function (handle) {
        return handle !== exclude;
      });
      if (this.handles.length === 0) return;

      this.limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 3, 1), MAX_STORED);
      this.root = (this.dataset.root || '/').replace(/\/+$/, '');
      this.requests = this.requests || {};

      var self = this;
      afterLoad(function () {
        self.whenNear(run, function () {
          onIdle(function () {
            self.load(run);
          });
        });
      });
    }

    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    // The element itself has no height while the section is hidden; a zero-height target still reports
    // intersection, so it is the anchor for both observers.
    whenNear(run, callback) {
      if (!this.alive(run)) return;
      if (!('IntersectionObserver' in window)) {
        callback();
        return;
      }
      var self = this;
      this.stopObserving();
      this.observer = new IntersectionObserver(
        function (entries) {
          if (!self.alive(run)) return;
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          self.stopObserving();
          callback();
        },
        { rootMargin: '800px 0px' }
      );
      this.observer.observe(this);
    }

    load(run) {
      if (!this.alive(run)) return;
      var self = this;
      var queue = this.handles.slice();
      var cards = [];
      var seen = {};

      // Fetch only as many as are still missing, in parallel; refill from the queue when some fail.
      var next = function () {
        if (!self.alive(run)) return;
        var missing = self.limit - cards.length;
        if (missing <= 0 || queue.length === 0) {
          self.render(cards);
          return;
        }
        Promise.all(queue.splice(0, missing).map(self.fetchCard, self)).then(function (results) {
          results.forEach(function (card) {
            if (!card || cards.length >= self.limit) return;
            var handle = card.getAttribute('data-handle');
            if (seen[handle]) return;
            seen[handle] = true;
            cards.push(card);
          });
          next();
        });
      };
      next();
    }

    // One request per handle for the element's lifetime: a run that starts over reuses the earlier answer.
    fetchCard(handle) {
      if (!this.requests[handle]) this.requests[handle] = this.request(handle);
      return this.requests[handle];
    }

    request(handle) {
      var url = this.root + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION;
      return fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(function (response) {
          if (response.ok) return response.text();
          if (response.body && response.body.cancel) response.body.cancel().catch(function () {});
          return null;
        })
        .then(function (html) {
          var result = html ? parseCard(html) : null;
          if (result && result.missing) forget(handle);
          return result && result.card ? result.card : null;
        })
        .catch(function () {
          return null;
        });
    }

    render(cards) {
      if (cards.length === 0) return;

      var fragment = document.createDocumentFragment();
      cards.forEach(function (card) {
        var item = document.createElement('li');
        item.className = 'iw-recent__item';
        item.appendChild(card);
        fragment.appendChild(item);
      });
      this.list.appendChild(fragment);
      this.rendered = true;

      var placeholder = this.querySelector('.iw-recent__placeholder');
      if (placeholder) placeholder.remove();

      if (this.designMode) {
        this.box.hidden = false;
        return;
      }
      this.revealWhenOffScreen();
    }

    revealWhenOffScreen() {
      var top = this.getBoundingClientRect().top;
      var onScreen = top >= 0 && top < window.innerHeight;
      if (!onScreen || !('IntersectionObserver' in window)) {
        this.reveal();
        return;
      }
      var self = this;
      var run = this.run;
      this.stopObserving();
      this.observer = new IntersectionObserver(function (entries) {
        if (!self.alive(run)) return;
        if (entries[entries.length - 1].isIntersecting) return;
        self.stopObserving();
        self.reveal();
      });
      this.observer.observe(this);
    }

    reveal() {
      if (!this.isConnected) return;
      var above = this.getBoundingClientRect().top < 0;
      var anchoring = window.CSS && window.CSS.supports && window.CSS.supports('overflow-anchor', 'auto');
      this.box.hidden = false;
      if (above && !anchoring) window.scrollBy(0, this.offsetHeight);
    }
  }

  window.customElements.define('iw-home-recent', IwHomeRecent);
})();
